import { z } from 'zod'

import { TYPE_LABELS } from '@/constants'
import type { CharacteristicValueType, OptionType } from '@/types'

export type CreateCharacteristicRequest = {
  description: string
  valueType: CharacteristicValueType
  useUnitOfMeasurement: boolean
  unitOfMeasurement?: string
  status?: 'DRAFT' | 'APPROVED'
}

const CHARACTERISTIC_VALUE_TYPES = [
  'NUMERIC',
  'BOOLEAN',
  'REFERENCE',
  'STRING',
  'SLOTTED_REFERENCE',
] as const satisfies readonly CharacteristicValueType[]

const isCharacteristicValueType = (
  value: string
): value is CharacteristicValueType =>
  CHARACTERISTIC_VALUE_TYPES.some((type) => type === value)

export const VALUE_TYPE_OPTIONS: OptionType[] = Object.entries(TYPE_LABELS)
  .filter(([value]) => isCharacteristicValueType(value))
  .map(([value, label]) => ({ value, label }))

const optionSchema = z.object({ value: z.string().min(1), label: z.string() })

export const characteristicCreationSchema = z
  .object({
    description: z.string().trim().min(1, 'Введите наименование'),
    valueType: z
      .object({ value: z.string(), label: z.string() })
      .refine((option) => isCharacteristicValueType(option.value), {
        message: 'Выберите тип значения',
      }),
    useUnitOfMeasurement: z.boolean(),
    unitOfMeasurement: z.union([optionSchema, z.literal('')]).optional(),
    approveImmediately: z.boolean(),
  })
  .superRefine((values, context) => {
    if (
      canUseUnitOfMeasurement(values.valueType.value) &&
      values.useUnitOfMeasurement &&
      !values.unitOfMeasurement
    )
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unitOfMeasurement'],
        message: 'Выберите единицу измерения',
      })
  })

export type CharacteristicCreationValues = z.infer<
  typeof characteristicCreationSchema
>

export const CHARACTERISTIC_CREATION_DEFAULTS: CharacteristicCreationValues = {
  description: '',
  valueType: { value: 'STRING', label: TYPE_LABELS.STRING },
  useUnitOfMeasurement: false,
  unitOfMeasurement: '',
  approveImmediately: true,
}

export function canUseUnitOfMeasurement(valueType: string) {
  return valueType !== 'BOOLEAN'
}

export const toCreateCharacteristicRequest = (
  values: CharacteristicCreationValues,
  isApprover: boolean
): CreateCharacteristicRequest => {
  const valueType = isCharacteristicValueType(values.valueType.value)
    ? values.valueType.value
    : 'STRING'
  const useUnit =
    canUseUnitOfMeasurement(valueType) && values.useUnitOfMeasurement
  return {
    description: values.description.trim(),
    valueType,
    useUnitOfMeasurement: useUnit,
    unitOfMeasurement:
      useUnit && values.unitOfMeasurement
        ? values.unitOfMeasurement.value
        : undefined,
    ...(isApprover
      ? { status: values.approveImmediately ? 'APPROVED' : 'DRAFT' }
      : {}),
  }
}
